let _dek: CryptoKey | null = null;

export class CannotDecryptError extends Error {
  constructor() {
    super("error.user.cannotDecrypt");
  }
}

export class CannotEncryptError extends Error {
  constructor() {
    super("error.user.cannotEncrypt");
  }
}

export class DekMissingError extends Error {
  constructor() {
    super("error.user.dekMissing");
  }
}

export function cleanUp() {
  _dek = null;
}

export async function decryptDek(
  passphrase: string,
  encryptedPrivateKeyBase64: string,
  ivBase64: string,
  saltBase64: string,
  encryptedDekBase64: string,
) {
  try {
    const encryptedPrivateKey = base64ToUint8Array(encryptedPrivateKeyBase64);
    const iv = base64ToUint8Array(ivBase64);
    const salt = base64ToUint8Array(saltBase64);
    const encryptedDek = base64ToUint8Array(encryptedDekBase64);

    const kek = await deriveKEK(passphrase, salt);

    const decryptedPrivateKeyBytes = await crypto.subtle.decrypt(
      { iv, name: "AES-GCM" },
      kek,
      encryptedPrivateKey,
    );

    const privateKey = await crypto.subtle.importKey(
      "pkcs8",
      decryptedPrivateKeyBytes,
      { hash: "SHA-256", name: "RSA-OAEP" },
      false,
      ["decrypt"],
    );

    const decryptedDekBytes = await crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      privateKey,
      encryptedDek,
    );

    const dek = await crypto.subtle.importKey(
      "raw",
      decryptedDekBytes,
      { length: 256, name: "AES-GCM" },
      false,
      ["encrypt", "decrypt"],
    );

    // Store DEK in the Service Worker
    _dek = dek;

    return;
  } catch (e) {
    console.error(e);
    throw new CannotDecryptError();
  }
}
//#endregion

//#region decryption
export async function decryptFromUrl(url: string, ivBase64: string) {
  const content = await fetchBinary(url);

  return await decryptBuffer(content, ivBase64);
}

//#region encryption
export async function encryptBuffer(content: ArrayBuffer) {
  if (!_dek) throw new DekMissingError();

  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      { iv, name: "AES-GCM" },
      _dek,
      content,
    );

    return {
      encrypted,
      iv: uint8ArrayToBase64(iv),
    };
  } catch (e) {
    console.error(e);
    throw new CannotEncryptError();
  }
}
//#endregion

export async function encryptFromUrl(url: string) {
  const content = await fetchBinary(url);

  return await encryptBuffer(content);
}

export async function generateKeys(passphrase: string) {
  const dek = await crypto.subtle.generateKey(
    { length: 256, name: "AES-GCM" },
    true, // We need to export keys
    ["encrypt", "decrypt"],
  );

  const keyPair = await crypto.subtle.generateKey(
    {
      hash: "SHA-256",
      modulusLength: 2048,
      name: "RSA-OAEP",
      publicExponent: new Uint8Array([1, 0, 1]),
    },
    true, // We need to export keys
    ["encrypt", "decrypt"],
  );

  const salt = crypto.getRandomValues(new Uint8Array(16));

  const kek = await deriveKEK(passphrase, salt);

  const iv = crypto.getRandomValues(new Uint8Array(12));

  const exportedPrivateKey = await crypto.subtle.exportKey(
    "pkcs8",
    keyPair.privateKey,
  );

  const encryptedPrivateKey = await crypto.subtle.encrypt(
    { iv, name: "AES-GCM" },
    kek,
    exportedPrivateKey,
  );

  const exportedDek = await crypto.subtle.exportKey("raw", dek);

  const encryptedDek = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    keyPair.publicKey,
    exportedDek,
  );

  const exportedPublicKey = await crypto.subtle.exportKey(
    "spki",
    keyPair.publicKey,
  );

  // Store keys in the Service Worker
  _dek = dek;

  // Return data to the main thread
  return {
    dek: arrayBufferToBase64(encryptedDek),
    iv: uint8ArrayToBase64(iv),
    privateKey: arrayBufferToBase64(encryptedPrivateKey),
    publicKey: arrayBufferToBase64(exportedPublicKey),
    salt: uint8ArrayToBase64(salt),
  };
}

//#region key management
export function isDEKAvailable() {
  return _dek !== null;
}

export async function rotatePassphrase(
  passphrase: string,
  oldPassphrase: string,
  encryptedPrivateKeyBase64: string,
  ivBase64: string,
  saltBase64: string,
) {
  try {
    let encryptedPrivateKey = base64ToArrayBuffer(encryptedPrivateKeyBase64);
    let iv = base64ToUint8Array(ivBase64);
    let salt = base64ToUint8Array(saltBase64);

    let kek = await deriveKEK(oldPassphrase, salt);

    const decryptedPrivateKeyBytes = await crypto.subtle.decrypt(
      { iv, name: "AES-GCM" },
      kek,
      encryptedPrivateKey,
    );

    salt = crypto.getRandomValues(new Uint8Array(16));

    kek = await deriveKEK(passphrase, salt);

    iv = crypto.getRandomValues(new Uint8Array(12));

    encryptedPrivateKey = await crypto.subtle.encrypt(
      { iv, name: "AES-GCM" },
      kek,
      decryptedPrivateKeyBytes,
    );

    return {
      iv: uint8ArrayToBase64(iv),
      privateKey: arrayBufferToBase64(encryptedPrivateKey),
      salt: uint8ArrayToBase64(salt),
    };
  } catch (e) {
    console.error(e);
    throw new CannotDecryptError();
  }
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  return uint8ArrayToBase64(bytes);
}

function base64ToArrayBuffer(base64: string) {
  return base64ToUint8Array(base64).buffer;
}
//#endregion

function base64ToUint8Array(base64: string) {
  const binaryString = atob(base64); // Decode Base64 to binary string
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

async function decryptBuffer(content: ArrayBuffer, ivBase64: string) {
  if (!_dek) throw new DekMissingError();

  const iv = base64ToUint8Array(ivBase64);

  try {
    const decrypted = await crypto.subtle.decrypt(
      { iv, name: "AES-GCM" },
      _dek,
      content,
    );

    return decrypted;
  } catch (e) {
    console.error(e);
    throw new CannotDecryptError();
  }
}

async function deriveKEK(passphrase: string, salt: Uint8Array) {
  const passphraseKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );

  const kek = await crypto.subtle.deriveKey(
    {
      hash: "SHA-256",
      iterations: 100000,
      name: "PBKDF2",
      salt,
    },
    passphraseKey,
    { length: 256, name: "AES-GCM" },
    false, // Non-extractable
    ["encrypt", "decrypt"],
  );

  return kek;
}

//#region helpers
async function fetchBinary(url: string) {
  const result = await fetch(url, {
    headers: {
      "Content-Type": "application/octet-stream",
    },
    method: "GET",
  });

  return await result.arrayBuffer();
}

function uint8ArrayToBase64(uint8Array: Uint8Array) {
  let binary = "";
  uint8Array.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

//#endregion
